import { useState } from 'react';
import { useAuth0 } from 'Auth';
import { postBlockListAttribute } from 'Shared/services/Reznor/PostBlockListAttribute';
import type { CreateBlockedAttributePayload } from 'Shared/services/Reznor/PostBlockListAttribute';
import { extractReznorError } from 'Shared/services/Reznor/extractReznorError';
import type { BlockListRow } from 'BlockList/types';

export type UseAddAttributeResult = {
  submitting: boolean;
  submitError: string | null;
  submit: (
    payload: CreateBlockedAttributePayload,
    callbacks: {
      onSuccess: (created: BlockListRow) => void;
      onError: (message: string) => void;
    }
  ) => Promise<void>;
};

export const useAddAttribute = (): UseAddAttributeResult => {
  const { getToken } = useAuth0();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const submit = async (
    payload: CreateBlockedAttributePayload,
    callbacks: {
      onSuccess: (created: BlockListRow) => void;
      onError: (message: string) => void;
    }
  ) => {
    setSubmitting(true);
    setSubmitError(null);

    try {
      const token = await getToken();
      const response = await postBlockListAttribute(token, payload);

      if (response && response.status === 201 && response.data) {
        callbacks.onSuccess(response.data);
      } else {
        const message = 'Failed to add blocked attribute.';
        setSubmitError(message);
        callbacks.onError(message);
      }
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: unknown };
        message?: string;
      };
      const message =
        extractReznorError(axiosErr.response?.data) ??
        axiosErr.message ??
        'Failed to add blocked attribute.';
      setSubmitError(message);
      callbacks.onError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return { submitting, submitError, submit };
};
